import React, { useState, useEffect, useCallback } from 'react';
import {
  ShoppingCart, Package, Factory, Truck, AlertTriangle, ArrowUpRight, TrendingUp,
  Loader2, CheckCircle, Clock, ArrowRight,
  ClipboardCheck, FileText, PackageOpen, Filter, ChevronDown, ChevronUp,
  Calendar, Building2, Briefcase, RefreshCw, X
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, Legend } from 'recharts';
import { purchaseAPI, stockAPI, productionAPI, dispatchAPI, quotationAPI, orderConfirmationAPI, dashboardAPI, branchAPI } from '../../services/api';
import DispatchKanban from './DispatchKanban';
import { RecentAmendments } from '../ui/RevisionHistory';

// ========== OVERVIEW TAB ==========
const OverviewTab = () => {
  const [stats, setStats] = useState({ pos: [], stock: [], production: [], dispatches: [], quotes: [], ocs: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [pos, stock, production, dispatches, quotes, ocs] = await Promise.all([
          purchaseAPI.getAll().catch(() => []),
          stockAPI.getAll().catch(() => []),
          productionAPI.getAll().catch(() => []),
          dispatchAPI.getAll().catch(() => []),
          quotationAPI.getAll().catch(() => []),
          orderConfirmationAPI.getAll().catch(() => []),
        ]);
        setStats({ pos, stock, production, dispatches, quotes, ocs });
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchStats();
  }, []);

  const totalPOValue = stats.pos.reduce((s, p) => s + (p.inr_value || 0), 0);
  const totalStockValue = stats.stock.reduce((s, i) => s + (i.value || 0), 0);
  const lowStockItems = stats.stock.filter(i => i.status === 'Low Stock' || i.status === 'Out of Stock').length;
  const activeProduction = stats.production.filter(p => p.status === 'In Progress').length;
  const pendingDispatches = stats.dispatches.filter(d => d.status === 'Packed' || d.status === 'In Transit').length;
  const totalQuoteValue = stats.quotes.reduce((s, q) => s + (q.total || 0), 0);

  const branchCostMap = {};
  stats.pos.forEach(po => {
    const branch = po.branch || 'Unknown';
    if (!branchCostMap[branch]) branchCostMap[branch] = { branch, Ordered: 0, Received: 0, InTransit: 0 };
    if (po.status === 'Received') branchCostMap[branch].Received += (po.inr_value || 0);
    else if (po.status === 'In Transit') branchCostMap[branch].InTransit += (po.inr_value || 0);
    else branchCostMap[branch].Ordered += (po.inr_value || 0);
  });
  const branchCostChart = Object.values(branchCostMap).map(b => ({
    branch: b.branch.length > 10 ? b.branch.substring(0, 10) : b.branch,
    Ordered: Math.round(b.Ordered / 100000 * 10) / 10,
    'In Transit': Math.round(b.InTransit / 100000 * 10) / 10,
    Received: Math.round(b.Received / 100000 * 10) / 10,
  }));

  const ocConfirmed = stats.ocs.filter(o => o.status === 'Confirmed').length;
  const ocReady = stats.ocs.filter(o => o.status === 'Ready to Dispatch').length;
  const ocTotal = stats.ocs.length;
  const ocTotalValue = stats.ocs.reduce((s, o) => s + (o.total || 0), 0);
  const ocReadyValue = stats.ocs.filter(o => o.status === 'Ready to Dispatch').reduce((s, o) => s + (o.total || 0), 0);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#24549c' }} />
      <span className="ml-2 text-sm text-gray-500">Loading dashboard...</span>
    </div>
  );

  return (
    <>
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6" data-testid="dashboard-kpi-cards">
        {[
          { label: 'Purchase Orders', value: `₹${(totalPOValue / 100000).toFixed(1)}L`, icon: TrendingUp, change: `${stats.pos.length} POs`, up: true, color: '#24549c' },
          { label: 'Quotations', value: `₹${(totalQuoteValue / 100000).toFixed(1)}L`, icon: ShoppingCart, change: `${stats.quotes.length} quotes`, up: true, color: '#3a6fc4' },
          { label: 'Stock Alerts', value: lowStockItems, icon: AlertTriangle, change: lowStockItems > 0 ? 'Action needed' : 'All good', up: lowStockItems === 0, color: '#E65100' },
          { label: 'In Production', value: activeProduction, icon: Factory, change: `${stats.production.length} total`, up: true, color: '#1a3d73' },
          { label: 'Pending Dispatch', value: pendingDispatches, icon: Truck, change: `${stats.dispatches.length} total`, up: true, color: '#3a6fc4' },
        ].map((c, i) => (
          <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-sm transition-all">
            <div className="flex items-center justify-between mb-3">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${c.color}12` }}>
                <c.icon className="w-4 h-4" style={{ color: c.color }} />
              </div>
              <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${c.up ? 'text-blue-600 bg-blue-50' : 'text-orange-600 bg-orange-50'}`}>
                {c.up && <ArrowUpRight className="w-2.5 h-2.5 inline" />}{c.change}
              </span>
            </div>
            <p className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{c.value}</p>
            <p className="text-[10px] text-gray-500 mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      {/* Dispatch Kanban Pipeline */}
      <DispatchKanban ocs={stats.ocs} dispatches={stats.dispatches} />

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-5 mb-6">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-2" style={{ fontFamily: 'DM Sans' }}>Inventory Summary</h3>
          <p className="text-[10px] text-gray-400 mb-4">Total stock value: ₹{(totalStockValue / 100000).toFixed(1)}L</p>
          <div className="space-y-3">
            {stats.stock.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-8">No stock entries yet.</p>
            ) : stats.stock.slice(0, 6).map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <p className="text-xs font-medium text-gray-900">{item.product_name}</p>
                  <p className="text-[10px] text-gray-400">{item.batch} | {item.quantity} {item.unit}</p>
                </div>
                <div className="text-right">
                  <p className="text-xs font-bold" style={{ color: '#24549c' }}>₹{(item.value || 0).toLocaleString()}</p>
                  <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${item.status === 'In Stock' ? 'bg-green-50 text-green-600' : item.status === 'Low Stock' ? 'bg-amber-50 text-amber-600' : 'bg-red-50 text-red-600'}`}>{item.status}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-2" style={{ fontFamily: 'DM Sans' }}>Branch-wise Purchase Cost (₹ Lakhs)</h3>
          <p className="text-[10px] text-gray-400 mb-4">Procurement spend by branch from live PO data</p>
          {branchCostChart.length === 0 ? (
            <p className="text-xs text-gray-400 text-center py-8">No purchase orders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={branchCostChart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="branch" tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                <YAxis tick={{ fontSize: 10, fill: '#9CA3AF' }} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '11px' }} />
                <Legend wrapperStyle={{ fontSize: '10px' }} />
                <Bar dataKey="Ordered" fill="#24549c" radius={[2, 2, 0, 0]} />
                <Bar dataKey="In Transit" fill="#FF9800" radius={[2, 2, 0, 0]} />
                <Bar dataKey="Received" fill="#4CAF50" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Recent Lists */}
      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>Recent Purchase Orders</h3>
          <div className="space-y-3">
            {stats.pos.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No purchase orders yet</p>
            ) : stats.pos.slice(0, 5).map((po, i) => (
              <div key={i} className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0 last:pb-0">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8eef7' }}>
                  <ShoppingCart className="w-3.5 h-3.5" style={{ color: '#24549c' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700">{po.po_number || po.id?.substring(0, 12)}</p>
                  <p className="text-[10px] text-gray-400">{po.vendor_name} | ₹{(po.inr_value || 0).toLocaleString()}</p>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${po.status === 'Received' ? 'bg-green-50 text-green-600' : po.status === 'In Transit' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'}`}>{po.status}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4" style={{ fontFamily: 'DM Sans' }}>Recent Production Orders</h3>
          <div className="space-y-3">
            {stats.production.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4">No production orders yet</p>
            ) : stats.production.slice(0, 5).map((prod, i) => (
              <div key={i} className="flex items-start gap-3 pb-3 border-b border-gray-50 last:border-0 last:pb-0">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#e8eef7' }}>
                  <Factory className="w-3.5 h-3.5" style={{ color: '#24549c' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700">{prod.product_name}</p>
                  <p className="text-[10px] text-gray-400">Qty: {prod.quantity} {prod.unit} | Branch: {prod.branch}</p>
                </div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-semibold ${prod.status === 'Completed' ? 'bg-green-50 text-green-600' : prod.status === 'In Progress' ? 'bg-amber-50 text-amber-600' : 'bg-blue-50 text-blue-600'}`}>{prod.status}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Amendments */}
      <div className="mt-5">
        <RecentAmendments limit={15} />
      </div>
    </>
  );
};

// ========== PROCUREMENT TRACKER TAB ==========
const PRIORITY_DOT = { High: 'bg-red-500', Medium: 'bg-amber-400', Low: 'bg-gray-400' };

const TrackerKPI = ({ label, value, icon: Icon, color, sub, onClick, active }) => (
  <button data-testid={`kpi-${label.replace(/\s/g, '-').toLowerCase()}`} onClick={onClick}
    className={`bg-white rounded-xl border p-4 text-left hover:shadow-md transition-all w-full ${active ? 'ring-2 shadow-md' : 'border-gray-100'}`}
    style={active ? { borderColor: color } : {}}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}14` }}>
        <Icon className="w-4.5 h-4.5" style={{ color }} />
      </div>
      {sub && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-gray-50 text-gray-500">{sub}</span>}
    </div>
    <p className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{value}</p>
    <p className="text-[10px] text-gray-500 mt-0.5">{label}</p>
  </button>
);

const ProcurementTrackerTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [activeSection, setActiveSection] = useState('pending_approvals');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ branch: '', department: '', date_from: '', date_to: '' });
  const [appliedFilters, setAppliedFilters] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (appliedFilters.branch) params.branch = appliedFilters.branch;
      if (appliedFilters.department) params.department = appliedFilters.department;
      if (appliedFilters.date_from) params.date_from = appliedFilters.date_from;
      if (appliedFilters.date_to) params.date_to = appliedFilters.date_to;
      setData(await dashboardAPI.getTracking(params));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [appliedFilters]);

  useEffect(() => { fetchData(); branchAPI.getAll().then(setBranches).catch(() => {}); }, [fetchData]);

  const applyFilters = () => { setAppliedFilters({ ...filters }); setShowFilters(false); };
  const clearFilters = () => { setFilters({ branch: '', department: '', date_from: '', date_to: '' }); setAppliedFilters({}); setShowFilters(false); };
  const activeFilterCount = Object.values(appliedFilters).filter(Boolean).length;
  const getBranchName = (branchId) => { const b = branches.find(x => x.id === branchId); return b ? b.name : (branchId || '').substring(0, 15); };

  if (loading && !data) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin" style={{ color: '#24549c' }} /></div>;

  const s = data?.summary || {};
  const sections = [
    { key: 'pending_approvals', label: 'Pending Approvals', value: s.pending_approvals || 0, icon: ClipboardCheck, color: '#7c3aed' },
    { key: 'open_indents', label: 'Open Indents', value: s.open_indents || 0, icon: FileText, color: '#2563eb' },
    { key: 'pending_pos', label: 'Pending POs', value: s.pending_pos || 0, icon: ShoppingCart, color: '#24549c', sub: s.total_pending_po_value ? `₹${(s.total_pending_po_value / 100000).toFixed(1)}L` : '' },
    { key: 'overdue_deliveries', label: 'Overdue Deliveries', value: s.overdue_deliveries || 0, icon: AlertTriangle, color: '#dc2626', sub: s.total_overdue_value ? `₹${(s.total_overdue_value / 100000).toFixed(1)}L` : '' },
    { key: 'partially_received', label: 'Partially Received', value: s.partially_received || 0, icon: PackageOpen, color: '#ea580c', sub: s.total_partial_value ? `₹${(s.total_partial_value / 100000).toFixed(1)}L` : '' },
  ];

  return (
    <>
      {/* Filter Controls */}
      <div className="flex items-center justify-end gap-2 mb-4">
        <button data-testid="refresh-dashboard" onClick={fetchData} disabled={loading} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
          <RefreshCw className={`w-4 h-4 text-gray-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button data-testid="toggle-filters" onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg border transition-all ${activeFilterCount > 0 ? 'bg-blue-50 border-blue-300 text-blue-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
          <Filter className="w-3.5 h-3.5" /> Filters
          {activeFilterCount > 0 && <span className="w-4 h-4 rounded-full bg-blue-600 text-white text-[9px] flex items-center justify-center">{activeFilterCount}</span>}
          {showFilters ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
      </div>

      {showFilters && (
        <div data-testid="filter-panel" className="bg-white rounded-xl border border-gray-200 p-4 mb-5 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div><label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1.5"><Building2 className="w-3 h-3 inline mr-1" />Branch</label>
              <select data-testid="filter-branch" value={filters.branch} onChange={e => setFilters({ ...filters, branch: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg">
                <option value="">All Branches</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select></div>
            <div><label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1.5"><Briefcase className="w-3 h-3 inline mr-1" />Department</label>
              <select data-testid="filter-department" value={filters.department} onChange={e => setFilters({ ...filters, department: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg">
                <option value="">All</option><option value="Purchase">Purchase</option><option value="Production">Production</option><option value="Sales">Sales</option><option value="Accounts">Accounts</option><option value="Factory">Factory</option>
              </select></div>
            <div><label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1.5"><Calendar className="w-3 h-3 inline mr-1" />From</label>
              <input data-testid="filter-date-from" type="date" value={filters.date_from} onChange={e => setFilters({ ...filters, date_from: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
            <div><label className="block text-[10px] font-semibold text-gray-500 uppercase mb-1.5"><Calendar className="w-3 h-3 inline mr-1" />To</label>
              <input data-testid="filter-date-to" type="date" value={filters.date_to} onChange={e => setFilters({ ...filters, date_to: e.target.value })} className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg" /></div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-gray-100">
            <button data-testid="clear-filters" onClick={clearFilters} className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700">Clear All</button>
            <button data-testid="apply-filters" onClick={applyFilters} className="px-4 py-1.5 text-xs font-semibold text-white rounded-lg" style={{ backgroundColor: '#24549c' }}>Apply</button>
          </div>
        </div>
      )}

      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <span className="text-[10px] text-gray-500 font-medium">Active:</span>
          {appliedFilters.branch && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-blue-50 text-blue-700 rounded-full">Branch: {getBranchName(appliedFilters.branch)}<button onClick={() => { const f = { ...appliedFilters }; delete f.branch; setAppliedFilters(f); }}><X className="w-2.5 h-2.5" /></button></span>}
          {appliedFilters.department && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-purple-50 text-purple-700 rounded-full">Dept: {appliedFilters.department}<button onClick={() => { const f = { ...appliedFilters }; delete f.department; setAppliedFilters(f); }}><X className="w-2.5 h-2.5" /></button></span>}
          {appliedFilters.date_from && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-green-50 text-green-700 rounded-full">From: {appliedFilters.date_from}<button onClick={() => { const f = { ...appliedFilters }; delete f.date_from; setAppliedFilters(f); }}><X className="w-2.5 h-2.5" /></button></span>}
          {appliedFilters.date_to && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium bg-green-50 text-green-700 rounded-full">To: {appliedFilters.date_to}<button onClick={() => { const f = { ...appliedFilters }; delete f.date_to; setAppliedFilters(f); }}><X className="w-2.5 h-2.5" /></button></span>}
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-6" data-testid="tracker-kpi-cards">
        {sections.map(sec => <TrackerKPI key={sec.key} {...sec} onClick={() => setActiveSection(sec.key)} active={activeSection === sec.key} />)}
      </div>

      {/* Detail Table */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-testid={`section-${activeSection}`}>
        <div className="px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>{sections.find(s => s.key === activeSection)?.label} ({sections.find(s => s.key === activeSection)?.value})</h3>
        </div>

        {activeSection === 'pending_approvals' && (
          <div className="divide-y divide-gray-50">
            {(data?.pending_approvals || []).length === 0 ? <div className="p-8 text-center"><ClipboardCheck className="w-8 h-8 text-gray-200 mx-auto mb-2" /><p className="text-xs text-gray-400">No pending approvals</p></div>
            : (data?.pending_approvals || []).map(appr => (
              <div key={appr.id} className="px-5 py-3 flex items-center gap-4 hover:bg-gray-50/50">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-purple-50"><ClipboardCheck className="w-4 h-4 text-purple-600" /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2"><span className="text-xs font-semibold text-gray-900">{appr.document_number}</span><span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700">{appr.document_type}</span></div>
                  <p className="text-[10px] text-gray-500 mt-0.5 truncate">{appr.document_detail}</p>
                </div>
                <span className="text-[10px] text-gray-400">{typeof appr.created_at === 'string' ? appr.created_at.substring(0, 10) : ''}</span>
              </div>
            ))}
          </div>
        )}

        {activeSection === 'open_indents' && (
          <div className="overflow-x-auto"><table className="w-full text-xs"><thead className="bg-gray-50/80"><tr>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Indent #</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Priority</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Purpose</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Branch</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Required By</th>
          </tr></thead><tbody className="divide-y divide-gray-50">
            {(data?.open_indents || []).length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-gray-400">No open indents</td></tr>
            : (data?.open_indents || []).map(ind => (
              <tr key={ind.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-2.5 font-medium text-gray-900 font-mono text-[11px]">{ind.indent_number || ind.id?.substring(0, 12)}</td>
                <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${ind.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{ind.status}</span></td>
                <td className="px-4 py-2.5"><span className="flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[ind.priority] || 'bg-gray-300'}`} />{ind.priority}</span></td>
                <td className="px-4 py-2.5 text-gray-600 max-w-[200px] truncate">{ind.purpose}</td>
                <td className="px-4 py-2.5 text-gray-500">{ind.branch_name || getBranchName(ind.branch)}</td>
                <td className="px-4 py-2.5"><span className={ind.is_overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}>{ind.required_by}</span>{ind.is_overdue && <span className="ml-1 text-[8px] px-1 py-0.5 bg-red-50 text-red-600 rounded font-bold">OVERDUE</span>}</td>
              </tr>
            ))}</tbody></table></div>
        )}

        {activeSection === 'pending_pos' && (
          <div className="overflow-x-auto"><table className="w-full text-xs"><thead className="bg-gray-50/80"><tr>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">PO Number</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Vendor</th>
            <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Value (INR)</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Expected Delivery</th>
          </tr></thead><tbody className="divide-y divide-gray-50">
            {(data?.pending_pos || []).length === 0 ? <tr><td colSpan={5} className="p-8 text-center text-gray-400">No pending POs</td></tr>
            : (data?.pending_pos || []).map(po => (
              <tr key={po.id} className={`hover:bg-gray-50/50 ${po.is_overdue ? 'bg-red-50/30' : ''}`}>
                <td className="px-4 py-2.5 font-medium text-gray-900 font-mono text-[11px]">{po.po_number || po.id?.substring(0, 12)}</td>
                <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${po.status === 'Ordered' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>{po.status}</span></td>
                <td className="px-4 py-2.5 text-gray-600">{po.vendor_name}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-gray-900">₹{(po.inr_value || 0).toLocaleString()}</td>
                <td className="px-4 py-2.5"><span className={po.is_overdue ? 'text-red-600 font-semibold' : 'text-gray-500'}>{po.expected_delivery}</span>{po.is_overdue && <span className="ml-1 text-[8px] px-1 py-0.5 bg-red-50 text-red-600 rounded font-bold">OVERDUE</span>}</td>
              </tr>
            ))}</tbody></table></div>
        )}

        {activeSection === 'overdue_deliveries' && (
          <div className="overflow-x-auto"><table className="w-full text-xs"><thead className="bg-gray-50/80"><tr>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">PO Number</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Status</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Vendor</th>
            <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Value (INR)</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Expected</th>
            <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Days Overdue</th>
          </tr></thead><tbody className="divide-y divide-gray-50">
            {(data?.overdue_deliveries || []).length === 0 ? <tr><td colSpan={6} className="p-8 text-center text-gray-400">No overdue deliveries</td></tr>
            : (data?.overdue_deliveries || []).map(po => (
              <tr key={po.id} className="hover:bg-red-50/30">
                <td className="px-4 py-2.5 font-medium text-gray-900 font-mono text-[11px]">{po.po_number || po.id?.substring(0, 12)}</td>
                <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[9px] font-semibold ${po.status === 'Partially Received' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>{po.status}</span></td>
                <td className="px-4 py-2.5 text-gray-600">{po.vendor_name}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-gray-900">₹{(po.inr_value || 0).toLocaleString()}</td>
                <td className="px-4 py-2.5 text-red-600 font-medium">{po.expected_delivery}</td>
                <td className="px-4 py-2.5 text-right"><span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold"><Clock className="w-3 h-3" /> {po.days_overdue}d</span></td>
              </tr>
            ))}</tbody></table></div>
        )}

        {activeSection === 'partially_received' && (
          <div className="overflow-x-auto"><table className="w-full text-xs"><thead className="bg-gray-50/80"><tr>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">PO Number</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Vendor</th>
            <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Value (INR)</th>
            <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Ordered</th>
            <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Received</th>
            <th className="text-right px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Remaining</th>
            <th className="text-left px-4 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">Progress</th>
          </tr></thead><tbody className="divide-y divide-gray-50">
            {(data?.partially_received || []).length === 0 ? <tr><td colSpan={7} className="p-8 text-center text-gray-400">No partially received orders</td></tr>
            : (data?.partially_received || []).map(po => (
              <tr key={po.id} className="hover:bg-gray-50/50">
                <td className="px-4 py-2.5 font-medium text-gray-900 font-mono text-[11px]">{po.po_number || po.id?.substring(0, 12)}</td>
                <td className="px-4 py-2.5 text-gray-600">{po.vendor_name}</td>
                <td className="px-4 py-2.5 text-right font-semibold text-gray-900">₹{(po.inr_value || 0).toLocaleString()}</td>
                <td className="px-4 py-2.5 text-right text-gray-700">{po.ordered_qty}</td>
                <td className="px-4 py-2.5 text-right text-emerald-600 font-semibold">{po.received_qty}</td>
                <td className="px-4 py-2.5 text-right text-orange-600 font-semibold">{po.remaining_qty}</td>
                <td className="px-4 py-2.5 w-40"><div className="flex items-center gap-2"><div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${po.receive_pct}%` }} /></div><span className="text-[10px] font-bold text-gray-600 w-8 text-right">{po.receive_pct}%</span></div></td>
              </tr>
            ))}</tbody></table></div>
        )}
      </div>
    </>
  );
};

// ========== MAIN DASHBOARD ==========
const DashboardOverview = () => {
  const [tab, setTab] = useState('overview');

  return (
    <div className="p-5 lg:p-6" data-testid="tracking-dashboard">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'DM Sans' }}>Dashboard</h1>
          <p className="text-xs text-gray-500 mt-0.5">Real-time view of your manufacturing operations</p>
        </div>
      </div>

      {/* Tab Switcher */}
      <div className="flex gap-1 mb-5 bg-gray-100 rounded-lg p-0.5 w-fit" data-testid="dashboard-tabs">
        {[
          { key: 'overview', label: 'Overview' },
          { key: 'tracker', label: 'Procurement Tracker' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`tab-${t.key}`}
            className={`px-4 py-2 text-xs font-semibold rounded-md transition-all ${tab === t.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab />}
      {tab === 'tracker' && <ProcurementTrackerTab />}
    </div>
  );
};

export default DashboardOverview;
